import clsx from "clsx";
import FooterDataDisclaimer from "components/company/FooterDataDisclaimer";
import { format } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import Link from "next/link";
import { useState } from "react";
import { Value } from "react-powerplug";
import { twMerge } from "tailwind-merge";
import { useAmplitude } from "../../../../lib/amplitude/Amplitude";
import useSelfie from "../../../../lib/hooks/useSelfie";
import filterUnionByTypename from "../../../../lib/utils/filterUnionByTypename";
import YearlyQuarterlySwitch from "../YearlyQuarterlySwitch";
import QuarterlyBankFinancials from "./banks/QuarterlyBankFinancials";
import YearlyBankFinancials from "./banks/YearlyBankFinancials";
import QuarterlyCompanyFinancials from "./companies/QuarterlyCompanyFinancials";
import YearlyCompanyFinancials from "./companies/YearlyCompanyFinancials";
import BrazilianCompanyFundamentalsChart from "./fundamentals/BrazilianCompanyFundamentalsChart";
import QuarterlyInsurerFinancials from "./insurers/QuarterlyInsurerFinancials";
import YearlyInsurerFinancials from "./insurers/YearlyInsurerFinancials";

interface IBrazilianCompanyFinancialsProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

export default function BrazilianCompanyFinancials({ issuer }: IBrazilianCompanyFinancialsProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();
  const [filterByStartOfPeriod, setFilterByStartOfPeriod] = useState<string | null>(null);

  if (!user) {
    return null;
  }

  const { yearly, quarterly } = issuer.financials;

  const yearlyBankFinancials = filterUnionByTypename(yearly, "YearlyBrazilianBankFinancialStatementRow");
  const yearlyCompanyFinancials = filterUnionByTypename(yearly, "YearlyBrazilianCompanyFinancialStatementRow");
  const yearlyInsurerFinancials = filterUnionByTypename(yearly, "YearlyBrazilianInsurerFinancialStatementRow");

  const quarterlyBankFinancials = filterUnionByTypename(quarterly, "QuarterlyBrazilianBankFinancialStatementRow");
  const quarterlyCompanyFinancials = filterUnionByTypename(quarterly, "QuarterlyBrazilianCompanyFinancialStatementRow");
  const quarterlyInsurerFinancials = filterUnionByTypename(quarterly, "QuarterlyBrazilianInsurerFinancialStatementRow");

  const isBank = yearlyBankFinancials.length > 0 || quarterlyBankFinancials.length > 0;
  const isCompany = yearlyCompanyFinancials.length > 0 || quarterlyCompanyFinancials.length > 0;
  const isInsurer = yearlyInsurerFinancials.length > 0 || quarterlyInsurerFinancials.length > 0;

  const hasYearlyAndQuarterly = quarterly.length > 0 && yearly.length > 0 && user.isPremium;

  // If the user is Premium and we only have Quarterly financials, display the Quarterly data by default
  let defaultSelectedFinancials: "YEARLY" | "QUARTERLY" = "YEARLY";
  if (yearly.length === 0 && user.isPremium && quarterly.length > 0) {
    defaultSelectedFinancials = "QUARTERLY";
  }

  const linkToPremium = (
    <Link
      href="/premium"
      onClick={() => {
        amplitude.logEvent("Click on link to become Premium", {
          origin: "Must be premium to view updated financials",
        });
      }}
      className="font-bold text-blue-600 hover:underline"
    >
      Premium
    </Link>
  );

  return (
    <Value<"YEARLY" | "QUARTERLY">
      initial={defaultSelectedFinancials}
      onChange={(period) => {
        amplitude.logEvent("Toggle financials", {
          tickerSymbol: issuer.tickerSymbolPrefix,
          period,
        });
      }}
    >
      {({ value, set }) => {
        return (
          <>
            {hasYearlyAndQuarterly && (
              <div
                css={{
                  textAlign: "center",
                }}
              >
                <YearlyQuarterlySwitch onChange={set} value={value} />
              </div>
            )}

            <div className="mx-auto">
              {value === "YEARLY" && (
                <div
                  css={{
                    position: "relative",
                    display: "inline-flex",
                    backgroundColor: "#ffffff",
                    borderRadius: 4,
                    boxShadow: "0 4px 6px 0 #33333333",
                  }}
                >
                  {isBank && <YearlyBankFinancials financials={yearlyBankFinancials} />}

                  {isCompany && (
                    <YearlyCompanyFinancials displayAllColumns={user.isPremium} financials={yearlyCompanyFinancials} />
                  )}

                  {isInsurer && (
                    <YearlyInsurerFinancials displayAllColumns={user.isPremium} financials={yearlyInsurerFinancials} />
                  )}
                </div>
              )}

              {value === "QUARTERLY" && (
                <div
                  css={{
                    position: "relative",
                    display: "inline-flex",
                    backgroundColor: "#ffffff",
                    borderRadius: 4,
                    boxShadow: "0 4px 6px 0 #33333333",
                  }}
                >
                  {isBank && (
                    <QuarterlyBankFinancials
                      financials={quarterlyBankFinancials.filter((financialStatementRow) => {
                        if (filterByStartOfPeriod !== null) {
                          return (
                            format(parseDateYmd(financialStatementRow.startOfPeriod), "dd/MM") ===
                            format(parseDateYmd(filterByStartOfPeriod), "dd/MM")
                          );
                        }

                        return true;
                      })}
                      onFilterByStartOfPeriod={(startOfPeriod) => {
                        setFilterByStartOfPeriod(filterByStartOfPeriod ? null : startOfPeriod);
                      }}
                    />
                  )}

                  {isCompany && (
                    <QuarterlyCompanyFinancials
                      displayAllColumns={user.isPremium}
                      financials={quarterlyCompanyFinancials.filter((financialStatementRow) => {
                        if (filterByStartOfPeriod !== null) {
                          return (
                            format(parseDateYmd(financialStatementRow.startOfPeriod), "dd/MM") ===
                            format(parseDateYmd(filterByStartOfPeriod), "dd/MM")
                          );
                        }

                        return true;
                      })}
                      onFilterByStartOfPeriod={(startOfPeriod) => {
                        setFilterByStartOfPeriod(filterByStartOfPeriod ? null : startOfPeriod);
                      }}
                    />
                  )}

                  {isInsurer && (
                    <QuarterlyInsurerFinancials
                      displayAllColumns={user.isPremium}
                      financials={quarterlyInsurerFinancials.filter((financialStatementRow) => {
                        if (filterByStartOfPeriod !== null) {
                          return (
                            format(parseDateYmd(financialStatementRow.startOfPeriod), "dd/MM") ===
                            format(parseDateYmd(filterByStartOfPeriod), "dd/MM")
                          );
                        }

                        return true;
                      })}
                      onFilterByStartOfPeriod={(startOfPeriod) => {
                        setFilterByStartOfPeriod(filterByStartOfPeriod ? null : startOfPeriod);
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            {yearly.length > 0 && (
              <div
                className={twMerge(
                  "mx-auto flex max-w-7xl justify-center",
                  clsx({
                    "max-w-5xl": isBank,
                  }),
                )}
              >
                <FooterDataDisclaimer>
                  {!user.isPremium ? (
                    <>
                      <strong>Para visualizar os valores trimestrais atualizados você deve ser {linkToPremium}!</strong>{" "}
                      Os valores acima estão em BRL (reais) e na escala de milhões—10.000 equivalem à 10 bilhões de
                      reais. <strong>P</strong> na tabela significa Prejuízo.
                    </>
                  ) : (
                    <>
                      Os valores acima estão em BRL (reais) e na escala de milhões—10.000 equivalem à 10 bilhões de
                      reais. <strong>P</strong> na tabela significa Prejuízo.
                    </>
                  )}
                </FooterDataDisclaimer>
              </div>
            )}

            {issuer.financials.yearly.length > 12 && hasYearlyAndQuarterly && (
              <div className="text-center">
                <YearlyQuarterlySwitch onChange={set} value={value} />
              </div>
            )}

            {(issuer.financials.yearly.length > 0 || issuer.financials.quarterly.length > 0) && (
              <BrazilianCompanyFundamentalsChart
                financials={
                  value === "YEARLY"
                    ? issuer.financials.yearly
                    : issuer.financials.quarterly.filter((financialStatementRow) => {
                        if (filterByStartOfPeriod !== null) {
                          return (
                            format(parseDateYmd(financialStatementRow.startOfPeriod), "dd/MM") ===
                            format(parseDateYmd(filterByStartOfPeriod), "dd/MM")
                          );
                        }

                        return true;
                      })
                }
              />
            )}
          </>
        );
      }}
    </Value>
  );
}
